import React from "react";
import { Link } from "react-router-dom";
import InnerHero from "../../components/InnerHero";
import {
  ArrowLeft,
  CheckCircle2,
  Cpu,
  Database,
  HardDrive,
  Laptop,
  Mail,
  Monitor,
  Network,
  ShieldCheck,
  Users,
  Wifi,
} from "lucide-react";

const Section = ({ id, title, icon: Icon, children, sub }) => (
  <section id={id} className="scroll-mt-32">
    <div className="flex items-end justify-between gap-4 mb-4 sm:mb-5">
      <div>
        <h3
          className="text-xl sm:text-2xl md:text-3xl text-brand font-semibold flex items-center gap-2"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {Icon && <Icon size={20} className="shrink-0" />} {title}
        </h3>
        {sub && <p className="text-sm text-[#3a3a3a]/80 italic mt-1 leading-relaxed">{sub}</p>}
      </div>
    </div>
    {children}
  </section>
);

const dashboard = [
  ["24x7", "Computing Support"],
  ["12", "AC Computer Labs"],
  ["500+", "Network Nodes"],
  ["2500+", "Users Supported"],
];

const staffLeads = [
  {
    name: "Prof. Ravindra T. Patil",
    designation: "Computer Center Head",
    email: "rtp@klsvdit.edu.in",
    image: "/FacultyCImages/ravindra_patil_AIML.jpeg",
  },
  {
    name: "Mr. Vijet Swadi",
    designation: "Computer Center In-charge",
    email: "vbs@klsvdit.edu.in",
    image: "/FacultyCImages/vijet_swadi_CSE.jpeg",
  },
];

const staff = [
  { sl: "1", name: "Mr. Vadiraj Udupi", designation: "Foreman", contact: "9900600160" },
  { sl: "2", name: "Mr. Jagadish Bendigeri", designation: "Instructor", contact: "9986592090" },
  { sl: "3", name: "Mr. Tanaji", designation: "Assistant Instructor", contact: "9945223643" },
  { sl: "4", name: "Mr. Sahadev Madar", designation: "Attender", contact: "08282-220261" },
  { sl: "5", name: "Mr. Nagesh Melinmani", designation: "Attender", contact: "7975753832" },
  { sl: "6", name: "Mr. Namdev Pagoji", designation: "Peon", contact: "8880627678" },
];

const infrastructure = [
  ["Computer Laboratories", "12 Well-equipped Air-Conditioned Laboratories"],
  ["Campus Area Covered", "27+ Acres"],
  ["Network Nodes", "500+ Connected Nodes"],
  ["Network Backbone", "Fiber Optic Infrastructure"],
  ["Supported Users", "2500+ Students, Faculty & Staff"],
  ["Security Solution", "Sophos Network Security"],
  ["Campus Connectivity", "LAN Access Across Campus"],
];

const labApplications = [
  "Linux Applications",
  "Windows Applications",
  "Programming and Development Tools",
  "Online Computer-Based Training (CBT)",
  "CAD/CAM Applications",
  "VLSI and Design Tools",
  "Network Simulation and Management Applications",
  "Database Management Systems",
  "Business and Enterprise Applications",
];

const itServices = [
  ["Central Computing Facility", "State-of-the-art computing environment"],
  ["Technical Support", "Hardware and software support services"],
  ["Licensed Software Support", "Access to genuine and licensed software packages"],
  ["High-End Computing Systems", "Advanced computing resources for academic use"],
  ["Server Infrastructure", "Dedicated server room with enterprise-grade security"],
  ["Network Security", "Cyberoam Firewall protection against external threats"],
  ["Campus Wi-Fi", "Wireless connectivity at strategic locations"],
  ["Hostel Wi-Fi", "Internet access available after academic hours"],
  ["CCTV Surveillance", "24/7 monitoring across campus and buses"],
  ["Microsoft Academic Alliance", "Educational software benefits for students and faculty"],
];

const networkFacilities = [
  ["High-Speed Internet", "500 Mbps OFC Leased Line"],
  ["Dedicated Internet Access", "24x7 Internet Connectivity"],
  ["Academic Internet Facility", "100 Mbps Dedicated Access"],
  ["Campus LAN", "Fiber Optic Backbone Network"],
  ["Wi-Fi Coverage", "Academic Blocks, Hostels & Key Locations"],
  ["Security Monitoring", "Sophos Security Suite & Firewall Protection"],
];

const softwareResources = [
  ["Microsoft Campus Agreement", "Available"],
  ["MSDN Academic Alliance License", "Available"],
  ["Linux Freeware Packages", "Available"],
  ["Windows Platform Applications", "Available"],
  ["Linux Platform Applications", "Available"],
  ["Database Management Software", "Available"],
  ["CAD/CAM Packages", "Available"],
  ["Programming Languages & Development Tools", "Available"],
  ["Antivirus & Security Tools", "Available"],
];

const keyHighlights = [
  "24x7 computing and network support throughout the year.",
  "12 modern air-conditioned computer laboratories.",
  "500+ network nodes connected through a fiber optic backbone.",
  "High-speed 500 Mbps leased-line Internet connectivity.",
  "Support for over 2500 users across the campus.",
  "Sophos-based network security infrastructure.",
  "Wi-Fi-enabled campus and hostel facilities.",
  "Cyberoam firewall-protected server infrastructure.",
  "Microsoft Academic Alliance access for students and faculty.",
  "Comprehensive collection of licensed software for academic and research activities.",
  "24/7 CCTV surveillance across the campus and institution buses.",
];

const quickFacts = [
  ["Facility", "Computer Center"],
  ["Availability", "24x7"],
  ["Computer Labs", "12"],
  ["Internet", "500 Mbps OFC Leased Line"],
  ["Academic Internet", "100 Mbps Dedicated Access"],
  ["Network Nodes", "500+"],
  ["Users Supported", "2500+"],
  ["Campus Area", "27+ Acres"],
  ["Network Backbone", "Fiber Optic Infrastructure"],
  ["Security", "Sophos Security Suite & Cyberoam Firewall"],
];

const ComputerCentre = () => {
  const navItems = [
    // ["overview", "Overview"],
    // ["staff", "Staff"],
    // ["infrastructure", "Infrastructure"],
    // ["applications", "Applications"],
    // ["services", "IT Services"],
    // ["network", "Network"],
    // ["software", "Software"],
    // ["highlights", "Highlights"],
  ];

  return (
    <main>
      <InnerHero
        title="Computer Center"
        image="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=2400&q=80"
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Campus Life", to: "/campus" },
          { label: "Computer Center" },
        ]}
      />

      <section className="bg-surface-alt/60 border-b border-brand/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex flex-wrap gap-x-4 sm:gap-x-6 gap-y-2 text-[11px] sm:text-xs font-sans-ui font-semibold tracking-[0.12em] sm:tracking-wider text-brand">
          <Link to="/campus" className="inline-flex items-center gap-2 uppercase hover:underline">
            <ArrowLeft size={14} className="shrink-0" /> Back to Campus Life
          </Link>
          {navItems.map(([h, l]) => (
            <a key={h} href={`#${h}`} className="uppercase hover:underline">
              {l}
            </a>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <div className="grid gap-8 lg:grid-cols-3 lg:gap-10">
          <div className="min-w-0 lg:col-span-2 space-y-8 sm:space-y-12">
            <Section id="overview" title="About the Computer Center" icon={Monitor}>
              <div className="bg-white border border-brand/15 p-4 sm:p-6 md:p-9">
                <span className="text-[11px] sm:text-xs tracking-[0.16em] sm:tracking-[0.2em] text-brand font-sans-ui font-semibold">
                  CAMPUS DIGITAL INFRASTRUCTURE
                </span>

                <div className="space-y-4 mt-4 text-[#2a2a2a]/85 leading-relaxed text-[15px] sm:text-[15.5px]">
                  <p>
                    The Computer Center (CC), often regarded as the neural system of the institution, provides uninterrupted computing and networking services 24x7 throughout the year. It serves as the backbone of the campus digital infrastructure by offering essential IT services such as Internet connectivity, Email services, Web hosting, DNS management, CCTV monitoring, Feedback systems, and Antivirus protection.
                  </p>
                  <p>
                    Established with the objective of creating a robust computational environment for academic and research activities, the Computer Center supports students, faculty, researchers, administrative offices, and staff.
                  </p>
                  <p>
                    In addition to infrastructure support, the center also offers training, technical assistance, and consultancy services. Advanced software solutions, including Sophos security services, are provided to ensure a secure and productive computing environment.
                  </p>
                  <p>
                    The Computer Center plays a pivotal role in fostering a technology-driven learning environment by providing reliable, secure, and advanced computing facilities that support the academic, research, and administrative needs of the institution.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-7">
                  {dashboard.map(([value, label]) => (
                    <div key={label} className="bg-[#7a1d2c] p-4 border border-[#7a1d2c] min-h-[90px] flex flex-col justify-center">
                      <div
                        className="text-2xl sm:text-3xl text-white font-semibold leading-tight"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {value}
                      </div>
                      <div className="text-[11px] sm:text-xs uppercase text-white/80 font-sans-ui tracking-[0.12em] sm:tracking-widest mt-2 leading-snug">
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            <Section id="staff" title="Computer Center Staff" icon={Users}>
              <div className="flex flex-col gap-4 sm:gap-5 mb-5 w-full">
                {staffLeads.map(({ name, designation, email, image }) => (
                  <div
                    key={name}
                    className="w-full bg-white border border-brand/10 p-4 sm:p-6 flex flex-col sm:flex-row items-start gap-4 sm:gap-6"
                  >
                    <img
                      src={image}
                      alt={`${designation} ${name}`}
                      className="w-full max-w-[180px] sm:w-36 md:w-40 h-auto aspect-[5/6] object-cover border border-brand/10"
                    />
                    <div className="min-w-0">
                      <h4
                        className="text-xl sm:text-2xl text-brand font-semibold"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {name}
                      </h4>
                      <p className="mt-2 text-[#2a2a2a]/85">{designation}</p>
                      <a
                        href={`mailto:${email}`}
                        className="inline-flex items-center gap-2 mt-3 text-sm text-brand hover:underline break-all"
                      >
                        <Mail size={14} className="shrink-0" />
                        {email}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto bg-white border border-brand/15">
                <table className="min-w-[640px] w-full border-collapse text-sm">
                  <thead className="bg-surface-alt/70">
                    <tr>
                      <th className="border border-brand/20 px-4 py-3 text-left">Sl. No.</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Staff Name</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Designation</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Contact Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(({ sl, name, designation, contact }) => (
                      <tr key={sl}>
                        <td className="border border-brand/20 px-4 py-3 font-semibold">{sl}</td>
                        <td className="border border-brand/20 px-4 py-3">{name}</td>
                        <td className="border border-brand/20 px-4 py-3">{designation}</td>
                        <td className="border border-brand/20 px-4 py-3">{contact}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section
              id="infrastructure"
              title="Infrastructure & Facilities"
              icon={Cpu}
              sub="Comprehensive computing infrastructure for academic, research, and laboratory requirements."
            >
              <div className="bg-white border border-brand/15 mb-5 overflow-hidden">
                <img
                  src="/COE/cyber.jpg"
                  alt="Computer Center infrastructure"
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>
              <div className="overflow-x-auto bg-white border border-brand/15">
                <table className="min-w-[560px] w-full border-collapse text-sm">
                  <thead className="bg-surface-alt/70">
                    <tr>
                      <th className="border border-brand/20 px-4 py-3 text-left">Facility</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {infrastructure.map(([facility, details]) => (
                      <tr key={facility}>
                        <td className="border border-brand/20 px-4 py-3 font-semibold">{facility}</td>
                        <td className="border border-brand/20 px-4 py-3">{details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section
              id="applications"
              title="Specialized Laboratory Applications"
              icon={Laptop}
              sub="Applications supported across academic and professional domains."
            >
              <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                {labApplications.map((item) => (
                  <div key={item} className="bg-white border border-brand/15 p-3 sm:p-4 flex gap-3">
                    <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#2a2a2a]/85 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section
              id="services"
              title="Major IT Services"
              icon={HardDrive}
              sub="Centralized hub for all information technology requirements of the institution."
            >
              <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                {itServices.map(([service, description]) => (
                  <div key={service} className="bg-white border border-brand/15 p-4 sm:p-5">
                    <h4 className="text-brand font-semibold">{service}</h4>
                    <p className="text-sm text-[#2a2a2a]/85 mt-2 leading-relaxed">
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="network" title="Network & Internet Facilities" icon={Network}>
              <div className="overflow-x-auto bg-white border border-brand/15">
                <table className="min-w-[560px] w-full border-collapse text-sm">
                  <thead className="bg-surface-alt/70">
                    <tr>
                      <th className="border border-brand/20 px-4 py-3 text-left">Facility</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Specification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {networkFacilities.map(([facility, specification]) => (
                      <tr key={facility}>
                        <td className="border border-brand/20 px-4 py-3 font-semibold">{facility}</td>
                        <td className="border border-brand/20 px-4 py-3">{specification}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section
              id="software"
              title="Software Resources"
              icon={Database}
              sub="Licensed and legal software resources for teaching, learning, research, and project development."
            >
              <div className="bg-white border border-brand/15 p-4 sm:p-5 mb-5">
                <p className="text-[#2a2a2a]/85 leading-relaxed text-[15px] sm:text-[15.5px]">
                  The Computer Center maintains a comprehensive collection of licensed and legal software to support teaching, learning, research, and project development across various engineering disciplines.
                </p>
              </div>

              <div className="overflow-x-auto bg-white border border-brand/15">
                <table className="min-w-[560px] w-full border-collapse text-sm">
                  <thead className="bg-surface-alt/70">
                    <tr>
                      <th className="border border-brand/20 px-4 py-3 text-left">Software Category</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Availability</th>
                    </tr>
                  </thead>
                  <tbody>
                    {softwareResources.map(([category, availability]) => (
                      <tr key={category}>
                        <td className="border border-brand/20 px-4 py-3 font-semibold">{category}</td>
                        <td className="border border-brand/20 px-4 py-3">{availability}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section id="highlights" title="Key Highlights" icon={ShieldCheck}>
              <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                {keyHighlights.map((item) => (
                  <div key={item} className="bg-white border border-brand/15 p-3 sm:p-4 flex gap-3">
                    <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#2a2a2a]/85 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          <aside className="space-y-5">
            <div className="bg-white border border-brand/15 p-4 sm:p-5 lg:sticky lg:top-36 self-start">
              <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
                Quick Facts
              </h4>

              <div className="mt-4 space-y-3 text-sm">
                {quickFacts.map(([label, value]) => (
                  <div key={label} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-brand flex-shrink-0 mt-0.5" />
                    <span className="text-[#2a2a2a]/85 leading-snug">
                      {label}: <strong className="text-[#2a2a2a]">{value}</strong>
                    </span>
                  </div>
                ))}
              </div>

              <a
                href="#staff"
                className="block mt-5 text-center bg-brand text-surface py-2.5 text-sm font-semibold font-sans-ui hover:bg-brand-dark transition"
              >
                View Staff Details
              </a>

              <div className="mt-6 pt-5 border-t border-brand/10">
                <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                  Core Services
                </h4>

                <ul className="space-y-2 text-sm text-[#2a2a2a]">
                  <li className="flex items-center gap-2">
                    <Wifi size={14} className="text-brand" />
                    Internet & Wi-Fi
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail size={14} className="text-brand" />
                    Email, Web Hosting & DNS
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-brand" />
                    CCTV, Antivirus & Firewall
                  </li>
                  <li className="flex items-center gap-2">
                    <Monitor size={14} className="text-brand" />
                    Technical Assistance
                  </li>
                  <li className="flex items-center gap-2">
                    <Laptop size={14} className="text-brand" />
                    Training & Consultancy
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default ComputerCentre;
